import { router } from "@inertiajs/react"
import { ArrowDown, ArrowUp, Pencil, Trash2 } from "lucide-react"

import { EditableCell } from "@/components/bids/EditableCell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from "@/components/ui/table"
import { addOnItemLabel, unitLabel } from "@/editor-core/labels"
import { EM_DASH, rate } from "@/lib/money"
import type { TemplateLine } from "@/types/bids"

const CONDITION_LABELS = { slab_only: "slab only", if_stairs: "if stairs" } as const

function ruleSummary(line: TemplateLine): string {
  const u = unitLabel(line.unit)
  if (line.calc_method === "count") return `${line.count_qty ?? 0} per job`
  if (line.calc_method === "manual") return `${line.manual_qty ?? 0} ${u} typed`
  const parts: string[] = []
  if (line.coverage_rate != null) {
    parts.push(`1 ${u} per ${line.coverage_rate} SF`)
  } else if (line.unit === "SY") {
    parts.push("job SF ÷ 9")
  } else {
    parts.push("per net SF")
  }
  if (line.min_qty != null) parts.push(`min ${line.min_qty}`)
  return parts.join(", ")
}

/** The draft's lines: reorder with ↑↓ (no drag dependency — an accepted
 *  deviation from the mock), inline unit-cost edits, per-line $/SF from the
 *  server, export toggles. */
export function TemplateLinesTable({
  lines,
  onEdit,
}: {
  lines: TemplateLine[]
  onEdit: (line: TemplateLine) => void
}) {
  const patch = (line: TemplateLine, attrs: Record<string, string | number | boolean | null>) =>
    router.patch(line.url, { order_template_line: attrs }, { preserveScroll: true })

  if (lines.length === 0) {
    return (
      <div className="callout" data-testid="template-lines-empty">
        <p>
          No lines yet. Everything a job needs beyond the level product goes here — installation
          labor, setting materials, trims — and rolls up into one $/SF adder.
        </p>
      </div>
    )
  }

  return (
    <div data-testid="template-lines">
      <Table>
        <TableHead>
          <tr>
            <TableHeadCell className="w-20">#</TableHeadCell>
            <TableHeadCell>Item</TableHeadCell>
            <TableHeadCell>Color / desc</TableHeadCell>
            <TableHeadCell>Quantity rule</TableHeadCell>
            <TableHeadCell>U/M</TableHeadCell>
            <TableHeadCell className="text-right">Unit cost</TableHeadCell>
            <TableHeadCell className="text-right">Freight</TableHeadCell>
            <TableHeadCell className="text-right">$/SF</TableHeadCell>
            <TableHeadCell>Export</TableHeadCell>
            <TableHeadCell className="w-20" />
          </tr>
        </TableHead>
        <TableBody>
          {lines.map((line, index) => (
            <TableRow key={line.id} data-testid={`template-line-${line.id}`}>
              <TableCell className="whitespace-nowrap">
                <span className="inline-flex items-center gap-1">
                  <span className="tabular-nums text-ink-muted">{index + 1}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    disabled={index === 0}
                    onClick={() => patch(line, { move: "up" })}
                    aria-label="Move line up"
                    data-testid={`line-move-up-${line.id}`}
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    disabled={index === lines.length - 1}
                    onClick={() => patch(line, { move: "down" })}
                    aria-label="Move line down"
                    data-testid={`line-move-down-${line.id}`}
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                </span>
              </TableCell>
              <TableCell>
                <span className="text-ink-display">{addOnItemLabel(line.item)}</span>
                {line.kind === "labor" && (
                  <Badge tone="muted" className="ml-2">
                    Labor
                  </Badge>
                )}
                {line.condition && (
                  <Badge tone="muted" className="ml-2">
                    {CONDITION_LABELS[line.condition]}
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-ink-muted">{line.description ?? EM_DASH}</TableCell>
              <TableCell>{ruleSummary(line)}</TableCell>
              <TableCell>{unitLabel(line.unit)}</TableCell>
              <TableCell className="text-right">
                <EditableCell
                  value={line.unit_cost}
                  display={rate(line.unit_cost)}
                  onCommit={(raw) => patch(line, { unit_cost: raw })}
                  testid={`line-unit-cost-${line.id}`}
                />
              </TableCell>
              <TableCell className="text-right">
                <EditableCell
                  value={line.freight_cost}
                  display={rate(line.freight_cost)}
                  onCommit={(raw) => patch(line, { freight_cost: raw })}
                  testid={`line-freight-${line.id}`}
                />
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {line.issue === "needs_coverage" ? (
                  <Badge tone="signal">needs coverage</Badge>
                ) : (
                  <span data-testid={`line-per-sf-${line.id}`}>{rate(line.per_sf)}</span>
                )}
              </TableCell>
              <TableCell>
                <Checkbox
                  checked={line.include_in_export}
                  onChange={(e) => patch(line, { include_in_export: e.target.checked })}
                  aria-label="Include on the ERP export"
                  data-testid={`line-export-${line.id}`}
                />
              </TableCell>
              <TableCell>
                <span className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => onEdit(line)}
                    aria-label="Edit line"
                    data-testid={`line-edit-${line.id}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => router.delete(line.url, { preserveScroll: true })}
                    aria-label="Remove line"
                    data-testid={`line-delete-${line.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
